import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { guesteriaAdminsService } from "@/services/guesteria-admins-service";
import { userKeys } from "@/components/pages/users/hooks/useUsers";
import type {
  CreateGuesteriaAdminDto,
  GuesteriaAdminListQuery,
  GuesteriaAdminTenantAccessReason,
  UpdateGuesteriaAdminDto,
} from "@/types/guesteria-admin";

export const guesteriaAdminKeys = {
  all: ["guesteria-admins"] as const,
  lists: () => [...guesteriaAdminKeys.all, "list"] as const,
  list: (query?: GuesteriaAdminListQuery) =>
    [...guesteriaAdminKeys.lists(), query] as const,
  filterOptions: () => [...guesteriaAdminKeys.all, "filter-options"] as const,
  details: () => [...guesteriaAdminKeys.all, "detail"] as const,
  detail: (id: string) => [...guesteriaAdminKeys.details(), id] as const,
  accessibleTenants: (
    id: string,
    query?: {
      accessReason?: GuesteriaAdminTenantAccessReason;
      limit?: number;
      offset?: number;
    },
  ) => [...guesteriaAdminKeys.detail(id), "tenants", query] as const,
};

export const useGuesteriaAdminById = (id: string, enabled = true) =>
  useQuery({
    queryKey: guesteriaAdminKeys.detail(id),
    queryFn: () => guesteriaAdminsService.getGuesteriaAdminById(id),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && Boolean(id),
  });

export const useGuesteriaAdmins = (
  query?: GuesteriaAdminListQuery,
  enabled = true,
) =>
  useQuery({
    queryKey: guesteriaAdminKeys.list(query),
    queryFn: () => guesteriaAdminsService.getGuesteriaAdmins(query),
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useGuesteriaAdminFilterOptions = (enabled = true) =>
  useQuery({
    queryKey: guesteriaAdminKeys.filterOptions(),
    queryFn: () => guesteriaAdminsService.getFilterOptions(),
    staleTime: 5 * 60 * 1000,
    enabled,
  });

export const useGuesteriaAdminAccessibleTenants = (
  id: string,
  query?: {
    accessReason?: GuesteriaAdminTenantAccessReason;
    limit?: number;
    offset?: number;
  },
  enabled = true,
) =>
  useQuery({
    queryKey: guesteriaAdminKeys.accessibleTenants(id, query),
    queryFn: () => guesteriaAdminsService.getAccessibleTenants(id, query),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && Boolean(id),
  });

export const useCreateGuesteriaAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateGuesteriaAdminDto) =>
      guesteriaAdminsService.createGuesteriaAdmin(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guesteriaAdminKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.counts() });
    },
  });
};

export const useUpdateGuesteriaAdmin = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateGuesteriaAdminDto) =>
      guesteriaAdminsService.updateGuesteriaAdmin(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guesteriaAdminKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: guesteriaAdminKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

export const useToggleGuesteriaAdminActive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      guesteriaAdminsService.setGuesteriaAdminActive(id, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guesteriaAdminKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};

export const useDeleteGuesteriaAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => guesteriaAdminsService.deleteGuesteriaAdmin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: guesteriaAdminKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.counts() });
    },
  });
};
